import React from 'react';
import {
  CollectionPreferences,
  CollectionPreferencesProps,
} from '@amzn/awsui-components-console';

import {
  COLUMN_DISPLAY_DND_LABELS,
  type TablePreferencesConfig,
  type TablePreferencesValue,
} from './tablePreferences';

/**
 * The preferences gear, shared by every table. Offers Cloudscape's standard set,
 * each wired to a Table prop by the consumer. Sticky columns is the one omission:
 * it needs a per-table option list, and these tables pin their first column.
 */

/** Dialog copy. `title` is also the gear's only accessible name. */
export const PREFERENCES_LABELS = {
  title: 'Preferences',
  confirmLabel: 'Confirm',
  cancelLabel: 'Cancel',
  closeAriaLabel: 'Close preferences',
  pageSizeTitle: 'Page size',
  wrapLinesLabel: 'Wrap lines',
  wrapLinesDescription: 'Select to see all the text and wrap the lines',
  stripedRowsLabel: 'Striped rows',
  stripedRowsDescription: 'Select to add alternating shaded rows',
  contentDensityLabel: 'Compact mode',
  contentDensityDescription:
    'Select to display content in a denser, more compact mode',
  contentDisplayTitle: 'Column preferences',
  contentDisplayDescription: 'Customize the columns visibility and order.',
} as const;

interface TablePreferencesProps {
  readonly config: TablePreferencesConfig;
  readonly preferences: TablePreferencesValue;
  /** Takes the RAW payload; `useTablePreferences` normalises it. */
  readonly onConfirm: (
    preferences: CollectionPreferencesProps.Preferences,
  ) => void;
}

/** The page-size list. "N rows" unless `config.pageSizeLabel` overrides it. */
function pageSizeOptions(
  config: TablePreferencesConfig,
): CollectionPreferencesProps.PageSizePreference['options'] | undefined {
  if (config.pageSizeOptions === undefined) return undefined;
  const label = config.pageSizeLabel ?? ((value: number) => `${value} rows`);
  return config.pageSizeOptions.map((value) => ({
    value,
    label: label(value),
  }));
}

const TablePreferences: React.FC<TablePreferencesProps> = ({
  config,
  preferences,
  onConfirm,
}) => {
  const sizeOptions = pageSizeOptions(config);

  return (
    <CollectionPreferences
      title={PREFERENCES_LABELS.title}
      confirmLabel={PREFERENCES_LABELS.confirmLabel}
      cancelLabel={PREFERENCES_LABELS.cancelLabel}
      closeAriaLabel={PREFERENCES_LABELS.closeAriaLabel}
      preferences={preferences}
      onConfirm={({ detail }) => onConfirm(detail)}
      // Omitted for an unpaginated table rather than shown empty.
      pageSizePreference={
        sizeOptions && {
          title: PREFERENCES_LABELS.pageSizeTitle,
          options: sizeOptions,
        }
      }
      wrapLinesPreference={{
        label: PREFERENCES_LABELS.wrapLinesLabel,
        description: PREFERENCES_LABELS.wrapLinesDescription,
      }}
      stripedRowsPreference={{
        label: PREFERENCES_LABELS.stripedRowsLabel,
        description: PREFERENCES_LABELS.stripedRowsDescription,
      }}
      contentDensityPreference={{
        label: PREFERENCES_LABELS.contentDensityLabel,
        description: PREFERENCES_LABELS.contentDensityDescription,
      }}
      contentDisplayPreference={{
        title: PREFERENCES_LABELS.contentDisplayTitle,
        description: PREFERENCES_LABELS.contentDisplayDescription,
        options: config.columnOptions,
        ...COLUMN_DISPLAY_DND_LABELS,
      }}
    />
  );
};

export default TablePreferences;
